import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout.js';
import { RequireAdmin } from './features/auth/RequireAdmin.js';

// Each leaf route uses lazy() so its bundle is split out and only fetched when
// the user navigates to it. Admin pages are wrapped in <RequireAdmin> at the
// element level so the gate runs before the lazy chunk is even fetched.

async function lazyComponent<T extends Record<string, unknown>>(
  loader: () => Promise<T>,
  pick: (mod: T) => () => JSX.Element,
): Promise<{ Component: () => JSX.Element }> {
  const mod = await loader();
  return { Component: pick(mod) };
}

function guard(Component: () => JSX.Element): () => JSX.Element {
  return function Guarded(): JSX.Element {
    return (
      <RequireAdmin>
        <Component />
      </RequireAdmin>
    );
  };
}

const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        index: true,
        lazy: () =>
          lazyComponent(
            () => import('./pages/HomePage.js'),
            (m) => m.HomePage,
          ),
      },
      {
        path: 'about',
        lazy: () =>
          lazyComponent(
            () => import('./pages/AboutPage.js'),
            (m) => m.AboutPage,
          ),
      },
      {
        path: 'photos',
        lazy: () =>
          lazyComponent(
            () => import('./pages/PhotosPage.js'),
            (m) => m.PhotosPage,
          ),
      },
      {
        path: 'videos',
        lazy: () =>
          lazyComponent(
            () => import('./pages/VideosPage.js'),
            (m) => m.VideosPage,
          ),
      },
      {
        path: 'events',
        lazy: () =>
          lazyComponent(
            () => import('./pages/EventsPage.js'),
            (m) => m.EventsPage,
          ),
      },
      {
        path: 'book',
        lazy: () =>
          lazyComponent(
            () => import('./pages/BookingPage.js'),
            (m) => m.BookingPage,
          ),
      },
      {
        path: 'admin/login',
        lazy: () =>
          lazyComponent(
            () => import('./pages/AdminLoginPage.js'),
            (m) => m.AdminLoginPage,
          ),
      },
      {
        path: 'admin',
        lazy: () =>
          lazyComponent(
            () => import('./pages/AdminPage.js'),
            (m) => guard(m.AdminPage),
          ),
      },
      {
        path: 'admin/photos',
        lazy: () =>
          lazyComponent(
            () => import('./pages/admin/AdminPhotosPage.js'),
            (m) => guard(m.AdminPhotosPage),
          ),
      },
      {
        path: 'admin/videos',
        lazy: () =>
          lazyComponent(
            () => import('./pages/admin/AdminVideosPage.js'),
            (m) => guard(m.AdminVideosPage),
          ),
      },
      {
        path: 'admin/events',
        lazy: () =>
          lazyComponent(
            () => import('./pages/admin/AdminEventsPage.js'),
            (m) => guard(m.AdminEventsPage),
          ),
      },
      {
        path: 'admin/site-text',
        lazy: () =>
          lazyComponent(
            () => import('./pages/admin/AdminSiteTextPage.js'),
            (m) => guard(m.AdminSiteTextPage),
          ),
      },
      {
        path: 'admin/about',
        lazy: () =>
          lazyComponent(
            () => import('./pages/admin/AdminAboutPage.js'),
            (m) => guard(m.AdminAboutPage),
          ),
      },
      {
        path: 'admin/site-assets',
        lazy: () =>
          lazyComponent(
            () => import('./pages/admin/AdminSiteAssetsPage.js'),
            (m) => guard(m.AdminSiteAssetsPage),
          ),
      },
      {
        path: 'admin/home-banner',
        lazy: () =>
          lazyComponent(
            () => import('./pages/admin/AdminHomeBannerPage.js'),
            (m) => guard(m.AdminHomeBannerPage),
          ),
      },
      {
        path: '*',
        lazy: () =>
          lazyComponent(
            () => import('./pages/NotFoundPage.js'),
            (m) => m.NotFoundPage,
          ),
      },
    ],
  },
];

const ROUTER_FUTURE_FLAGS = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

export function createAppRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(routes, { future: ROUTER_FUTURE_FLAGS });
}

export { routes, ROUTER_FUTURE_FLAGS };
